// Catalog.ID Client — Account-Blob Keys and Payload Encryption
// ============================================================================
// The cryptographic half of the account blob store (cid-encrypted-storage
// §4.1). Pure and DOM-free: no network, no key bag, no modals — those live in
// `state/cid-blobs.ts` (the vault's own blobs) and `key-grant/blob-handlers.ts`
// (a module's). Keeping them apart is what lets the key schedule be pinned by
// a unit test with no environment at all.
//
// ── The key ─────────────────────────────────────────────────────────────────
//   K = HKDF-SHA512(derive_root, "catalog-cid-derive-v1",
//                   moduleId ‖ 0x00 ‖ "blob:<name>:v1"), 32 bytes
//
// ONE INFO STRING PER BLOB, versioned. That is what lets a single blob's key
// be rotated (bump `:v1` to `:v2`, re-encrypt that document) without touching
// any other blob of the same module. The module prefix is applied by the
// vault, never by the caller, so a module cannot reach another's documents.
//
// ── The invariant this file serves ──────────────────────────────────────────
// Derived keys live in MEMORY ONLY (§4.1). Nothing here writes a key anywhere;
// callers derive when they have something to push or pull and zero the buffer
// afterwards. A derived key is never wrapped into the OS keyvault, never put in
// a bag entry, and never device-copied: it is reproducible on any unlocked
// device from the root, so persisting it buys nothing and costs a second copy.
//
// The server sees `{ciphertext, nonce}` and a size. It cannot read a blob, and
// neither can any module but the one whose namespace it is in.

import * as aes from './aes';
import { deriveModuleKey } from './derive';

/**
 * Blob names, matching the server's route segment pattern exactly
 * (`AccountBlobController.NamePattern`). Lowercase alphanumerics and dashes,
 * 1–40 characters, not dash-initial: the name is a path segment AND a KDF
 * input, so anything that could carry a separator or a case-fold collision is
 * refused here rather than answered 400 after a round trip.
 */
const BLOB_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

export function isValidBlobName(name: string): boolean {
    return BLOB_NAME_PATTERN.test(name);
}

/**
 * Ceiling on the stored base64 ciphertext of ONE blob, mirroring the server's
 * `Blobs:MaxBlobBase64Bytes` default (§4.2). Checked client-side so an oversize
 * document fails before a quarter-megabyte crosses the dashboard bus, not
 * after the server answers 413.
 */
export const MAX_BLOB_BASE64_CHARS = 262144;

/** Nonce width for AES-256-GCM, in bytes. */
const BLOB_NONCE_BYTES = 12;

/** Key width. Anything else is a caller bug, not a cipher parameter. */
const BLOB_KEY_BYTES = 32;

/**
 * The `info` bytes a blob's key is derived under: `utf8("blob:" ‖ name ‖ ":v1")`.
 * Exported so the pinned vectors (and any future .NET mirror) can show the
 * exact bytes without re-deriving.
 */
export function blobInfo(name: string): Uint8Array {
    return aes.encodeUtf8(`blob:${name}:v1`);
}

/**
 * The 32-byte key for `<moduleId>/<name>`.
 *
 * @param root     the account's 32-byte derive root
 * @param moduleId the namespace — the vault's own `cid`, or a hub-attested sender
 * @param name     the blob name, validated here so a bad name can never reach the KDF
 */
export function deriveBlobKey(root: Uint8Array, moduleId: string, name: string): Uint8Array {
    if (!isValidBlobName(name)) {
        throw new Error(`blob: illegal blob name '${name}'`);
    }
    return deriveModuleKey(root, moduleId, blobInfo(name));
}

/** AES-256-GCM under `key` with a FRESH 96-bit nonce. Both halves base64. */
export async function encryptBlob(
    key: Uint8Array,
    plaintext: Uint8Array,
): Promise<{ ciphertext: string; nonce: string }> {
    if (key.length !== BLOB_KEY_BYTES) {
        throw new Error(`blob: key must be ${BLOB_KEY_BYTES} bytes (got ${key.length})`);
    }
    const nonce = crypto.getRandomValues(new Uint8Array(BLOB_NONCE_BYTES));
    const ciphertext = await aes.encrypt(key, nonce, plaintext);
    return { ciphertext: aes.toBase64(ciphertext), nonce: aes.toBase64(nonce) };
}

/**
 * Open a stored blob. Throws on ANY mismatch — wrong key, tampered ciphertext,
 * tampered nonce — because AES-GCM's tag is the only integrity check the store
 * has: the server never sees the plaintext and cannot vouch for it.
 */
export async function decryptBlob(
    key: Uint8Array,
    ciphertext: string,
    nonce: string,
): Promise<Uint8Array> {
    if (key.length !== BLOB_KEY_BYTES) {
        throw new Error(`blob: key must be ${BLOB_KEY_BYTES} bytes (got ${key.length})`);
    }
    return aes.decrypt(key, aes.fromBase64(nonce), aes.fromBase64(ciphertext));
}
